import { motion } from "framer-motion";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { 
  FileText, 
  Calendar, 
  CheckCircle, 
  Download,
  ChevronRight,
  Phone,
  GraduationCap
} from "lucide-react";
import { AdmissionFormDialog } from "../components/forms/AdmissionFormDialog";

const admissionSteps = [
  {
    step: 1,
    title: "Submit Enquiry",
    description: "Fill out the admission enquiry form with basic details",
  },
  {
    step: 2,
    title: "Document Verification",
    description: "Submit required documents for verification",
  },
  {
    step: 3,
    title: "Interaction Round",
    description: "Student interaction and parent counseling session",
  },
  {
    step: 4,
    title: "Admission Confirmation",
    description: "Complete fee payment and confirm admission",
  },
];

const eligibility = [
  { class: "Pre-Nursery", age: "2.5 - 3 years", criteria: "Age appropriate development" },
  { class: "Nursery", age: "3 - 4 years", criteria: "Age appropriate development" },
  { class: "LKG", age: "4 - 5 years", criteria: "Age appropriate development" },
  { class: "UKG", age: "5 - 6 years", criteria: "Age appropriate development" },
  { class: "Class 1", age: "5.5 - 6.5 years", criteria: "Kindergarten completion" },
  { class: "Class 2-5", age: "As per age criteria", criteria: "Previous class completion" },
  { class: "Class 6-8", age: "As per age criteria", criteria: "Previous class completion + Assessment" },
  { class: "Class 9-10", age: "As per age criteria", criteria: "Previous class completion + Assessment" },
];

const documents = [
  "Birth Certificate (Original + Copy)",
  "Previous School Transfer Certificate",
  "Previous Year Mark Sheet",
  "Passport Size Photographs (6)",
  "Aadhar Card (Student & Parents)",
  "Address Proof",
  "Caste Certificate (if applicable)",
  "Medical Fitness Certificate",
];

const feeStructure = [
  { class: "Pre-Nursery to UKG", admission: "₹15,000", tuition: "₹3,500/month" },
  { class: "Class 1 to 5", admission: "₹20,000", tuition: "₹4,000/month" },
  { class: "Class 6 to 8", admission: "₹25,000", tuition: "₹4,500/month" },
  { class: "Class 9 to 10", admission: "₹30,000", tuition: "₹5,000/month" },
];

const Admission = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div data-testid="admission-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Admissions
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Join the ID4U School family - Admissions open for 2024-25
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <Button 
                onClick={() => setIsFormOpen(true)}
                className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8 py-6 text-lg"
                data-testid="admission-apply-btn"
              >
                Apply Now
                <ChevronRight className="ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="bg-white/10 border-white text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg"
              >
                <Download className="mr-2" />
                Download Prospectus
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              How to Apply
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Admission Process
            </h2>
            <div className="section-divider mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {admissionSteps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-slate-50 p-6 rounded-2xl h-full">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ChevronRight className="text-primary" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Requirements
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Eligibility Criteria
            </h2>
            <div className="section-divider mx-auto"></div>
          </motion.div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-medium">Class</th>
                    <th className="px-6 py-4 text-left font-medium">Age Requirement</th>
                    <th className="px-6 py-4 text-left font-medium">Eligibility Criteria</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibility.map((item, index) => (
                    <tr key={item.class} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="px-6 py-4 font-medium text-gray-900">{item.class}</td>
                      <td className="px-6 py-4 text-gray-600">{item.age}</td>
                      <td className="px-6 py-4 text-gray-600">{item.criteria}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-secondary font-medium text-sm uppercase tracking-wider">
                Checklist
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Documents Required
              </h2>
              <p className="text-gray-600 mb-8">
                Please ensure you have the following documents ready when applying for admission:
              </p>
              <div className="space-y-3">
                {documents.map((doc, index) => (
                  <motion.div
                    key={doc}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{doc}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=800&q=80"
                alt="Documents"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fee Structure */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Investment in Future
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Fee Structure (Demo)
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Transparent fee structure for quality education
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {feeStructure.map((item, index) => (
              <motion.div
                key={item.class}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <GraduationCap className="text-primary" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                  {item.class}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Admission Fee</span>
                    <span className="font-semibold text-primary">{item.admission}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Tuition Fee</span>
                    <span className="font-semibold text-primary">{item.tuition}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            * Additional charges may apply for transport, books, and uniforms. Contact office for detailed fee breakdown.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Join ID4U School?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Take the first step towards your child's bright future. Apply now or contact us for more information.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                onClick={() => setIsFormOpen(true)}
                className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8 py-6 text-lg"
              >
                Apply Now
                <ChevronRight className="ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="bg-white/10 border-white text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg"
                asChild
              >
                <a href="tel:+919281410305">
                  <Phone className="mr-2" />
                  Call Us
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <AdmissionFormDialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default Admission;
